import ImageLayer from 'ol/layer/Image';
import ImageSource from 'ol/source/Image';
import GeomodImageWMS from '../sources/GeomodImageWMS';
import GeomodTileRasterTMS, { PAINT_FIRST_GROUP } from '../sources/GeomodTileRasterTMS';
import { S52_FIRST_GROUP_COLORS } from '../utils/S52Colors';
import AutoRefreshStrategy from '../utils/AutoRefreshStrategy';

/**
 * The options passed to the super class to build it.
 * @type {Array<string>}
 */
const INHERITED_OPTIONS = ['type', 'filter', 'title', 'opacity', 'visible', 'extent', 'minResolution', 'maxResolution', 'source', 'map', 'minZoom', 'maxZoom'];

/**
 * @typedef {Object} GeomodImageLayerAdditionnalOptions
 * @property {string} [type] If base layer, value is base.
 * @property {string} [filter] Filter name of the layer group in layerswitcher.
 * @property {string} [title] The title of the layer group in the layer switcher.
 * @property {number} [autoRefresh] Duration between to refresh.
 * @property {number} [opacity] Opacity (0, 1). Default 1
 * @property {boolean} [visible] Visibility. Default true
 * @property {import('ol/extent').Extent} [extent] The bounding extent for layer rendering. The layer will not be rendered outside of this extent.
 * @property {number} [minResolution] The minimum resolution (inclusive) at which this layer will be visible.
 * @property {number} [maxResolution] The maximum resolution (exclusive) below which this layer will be visible.
 * @property {import('ol/source/Tile').default} [source] Source for this layer.
 * @property {import('ol/PluggableMap').default} [map] Sets the layer as overlay on a map. The map will not manage this layer in its layers collection, and the layer will be rendered on top. This is useful for temporary layers. The standard way to add a layer to a map and have it managed by the map is to use module:ol/Map#addLayer.
 * @property {Map<string,string>} [additionals] Additionals parameters for the layer.
 * @property {number} [minZoom] The minimum view zoom level (exclusive) above which this layer will be visible.
 * @property {number} [maxZom] The maximum view zoom level (inclusive) at which this layer will be visible.
 */

/**
 * @typedef {GeomodImageLayerAdditionnalOptions|import("../sources/GeomodImageWMS").GeomodImageWMSOptions} GeomodImageLayerOptions
 */

/**
 * The Geomod image layer. It create a {@link gm.source.GeomodImageWMS} if source not provide.
 * @extends {ImageLayer}
 */
export default class GeomodImageLayer extends ImageLayer {
  /**
     * @constructor
     * @param {GeomodImageLayerOptions} options The options
     */
  constructor(options = {}) {
    const opts = { ...options };
    if (options.source === undefined || !(options.source instanceof ImageSource)) {
      switch (options.sourceType) {
        case 'TileRasterWMS':
          opts.source = new GeomodTileRasterTMS({ ...options, lib: S52_FIRST_GROUP_COLORS, operation: PAINT_FIRST_GROUP });
          break;
        case 'ImageWMS':
        default:
          opts.source = new GeomodImageWMS(options);
          break;
      }
    }

    const superOptions = {};
    for (const key of INHERITED_OPTIONS) {
      superOptions[key] = opts[key];
    }
    super(superOptions);

    if (options.autoRefresh) {
      this.autoRefresh = new AutoRefreshStrategy(this, options.autoRefresh);
      this.autoRefresh.start();
    }

    if (options.additionals) {
      this.values_ = { ...options.additionals, ...this.values_ };
    }
  }

  /**
     * Add a parameter for the layer.
     * @param {string} paramName Parameter name.
     * @param {string} newValue Parameter value.
     */
  addParam(paramName, value) {
    const source = this.getSource();
    source.updateParams({ [paramName]: value });
  }

  /**
     * Update a parameter if it exists.
     * @param {string} paramName Parameter name.
     * @param {string} newValue Parameter value.
     */
  updateParam(paramName, newValue) {
    const source = this.getSource();
    if (source.getParams()[paramName]) {
      source.updateParams({ [paramName]: newValue });
    }
  }

  /**
     * Reomve a parameter if it exists.
     * @param {string} paramName Parameter name.
     */
  removeParam(paramName) {
    const source = this.getSource();
    if (source.getParams()[paramName]) {
      delete source.getParams()[paramName];
      source.updateParams({});
    }
  }

  /**
     * Refresh the layer by requesting a new image in server.
     */
  refresh() {
    this.getSource().refresh();
  }

  /**
     * Get the layer name.
     * @returns {string} the layers name
     */
  getName() {
    return this.getSource().getParams().LAYERS;
  }

  /**
     * Get the layer tag.
     * @return {string} the layers tag
     */
  getTag() {
    return this.getSource().getTag();
  }
}
